import React, { useState, useEffect, useCallback } from 'react';
import { Workflow, RefreshCw, Save, Info, RotateCcw } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface SelectableModel {
  id: string;
  provider: string;
  displayName: string;
  description: string | null;
}

interface StageOverride {
  model?: string;
  thinking?: string;
}

interface PipelineStage {
  stage: string;
  label: string;
  notes: string | null;
  ai_call: boolean;
  cross_vendor: boolean;
  default_model: string | null;
  default_thinking: string;
  fallback_model: string | null;
  override: StageOverride | null;
  effective_model: string | null;
  effective_thinking: string;
  effective_provider: string | null;
  source: string;
}

const SOURCE_LABELS: Record<string, string> = {
  default: 'Default',
  tenant_override: 'Overridden',
  fallback: 'Fallback in use',
  deterministic: 'No model',
  caller: 'Caller model',
  unknown_stage: 'Unknown',
};

const SOURCE_STYLES: Record<string, string> = {
  default: 'bg-gray-100 text-gray-700',
  tenant_override: 'bg-blue-100 text-blue-700',
  fallback: 'bg-amber-100 text-amber-800',
  deterministic: 'bg-slate-100 text-slate-500',
  caller: 'bg-amber-100 text-amber-800',
  unknown_stage: 'bg-red-100 text-red-700',
};

const SOPPipelineModels: React.FC = () => {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [models, setModels] = useState<SelectableModel[]>([]);
  const [thinkingLevels, setThinkingLevels] = useState<string[]>([]);
  const [draft, setDraft] = useState<Record<string, StageOverride>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/sop-pipeline-models');
      const data = res.data?.data;
      setStages(data?.stages || []);
      setModels(data?.selectable_models || []);
      setThinkingLevels(data?.thinking_levels || []);

      const existing: Record<string, StageOverride> = {};
      (data?.stages || []).forEach((stage: PipelineStage) => {
        if (stage.override) existing[stage.stage] = { ...stage.override };
      });
      setDraft(existing);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not load the pipeline model configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // An empty model or thinking value means "no override" — the key is dropped
  // rather than saved as blank, so the stage goes back to its declared default.
  const setOverride = (stage: string, field: keyof StageOverride, value: string) => {
    setDraft(prev => {
      const next = { ...prev };
      const entry = { ...(next[stage] || {}) };
      if (value) entry[field] = value;
      else delete entry[field];
      if (Object.keys(entry).length) next[stage] = entry;
      else delete next[stage];
      return next;
    });
  };

  const clearStage = (stage: string) => {
    setDraft(prev => {
      const next = { ...prev };
      delete next[stage];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/sop-pipeline-models', { stages: draft });
      toast.success('Pipeline model configuration saved');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save the pipeline model configuration');
    } finally {
      setSaving(false);
    }
  };

  const overrideCount = Object.keys(draft).length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-start space-x-3">
          <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg">
            <Workflow className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">SOP Builder Pipeline Models</h2>
            <p className="text-gray-600 text-sm mt-1">
              Every stage of the SOP Builder pipeline and the model it runs on. Leave a stage blank to use its default.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading || saving}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={save}
            disabled={loading || saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : `Save${overrideCount ? ` (${overrideCount})` : ''}`}
          </button>
        </div>
      </div>

      <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-xs text-blue-800 space-y-1">
          <p>Overrides apply to this tenant only and take effect within a minute — no deploy needed.</p>
          <p>If a chosen model is not available, the stage automatically drops to its fallback rather than failing.</p>
          <p>Extended thinking only applies to Anthropic models, and not to the L3 structured-output path, which pins a tool call.</p>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading pipeline stages...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-3 pr-4">Stage</th>
                <th className="py-3 pr-4">Running now</th>
                <th className="py-3 pr-4">Model override</th>
                <th className="py-3 pr-4">Thinking</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {stages.map(stage => (
                <tr key={stage.stage} className="border-b border-gray-100 align-top">
                  <td className="py-3 pr-4">
                    <div className="font-medium text-gray-900">{stage.label}</div>
                    {stage.notes && <div className="text-xs text-gray-500 mt-1 max-w-md">{stage.notes}</div>}
                    {stage.cross_vendor && (
                      <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                        Different vendor from the generator
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    {stage.ai_call ? (
                      <>
                        <div className="text-gray-900">{stage.effective_model || '—'}</div>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className={`text-[11px] px-2 py-0.5 rounded ${SOURCE_STYLES[stage.source] || 'bg-gray-100 text-gray-700'}`}>
                            {SOURCE_LABELS[stage.source] || stage.source}
                          </span>
                          {stage.effective_thinking !== 'off' && (
                            <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                              thinking: {stage.effective_thinking}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-400">Deterministic — no model call</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <select
                      disabled={!stage.ai_call}
                      value={draft[stage.stage]?.model || ''}
                      onChange={e => setOverride(stage.stage, 'model', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm min-w-[200px] disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">Default ({stage.default_model || 'none'})</option>
                      {models.map(model => (
                        <option key={model.id} value={model.id}>
                          {model.displayName} · {model.provider}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 pr-4">
                    <select
                      disabled={!stage.ai_call}
                      value={draft[stage.stage]?.thinking || ''}
                      onChange={e => setOverride(stage.stage, 'thinking', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">Default ({stage.default_thinking})</option>
                      {thinkingLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 pr-4">
                    {draft[stage.stage] && (
                      <button
                        onClick={() => clearStage(stage.stage)}
                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                        title="Clear this override"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SOPPipelineModels;
